import GridTable from '../../table/GridTable';
import MobileAccordionTable from '../../table/MobileAccordionTable';
import { ColDef, RowDoubleClickedEvent } from 'ag-grid-community';
import { IOrderTable, useOrderStore } from '../../../store/useOrder';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import OrderListFilter from './filter/OrderListFilter';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import ConfirmModal from './ConfirmModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import ConfirmDialog from "../../modal/ConfirmDialog";
import { useLoginStore } from '../../../store/useLogin';
import { useFranchiseStore } from '../../../store/useFranchise';
import useIsMobile from '../../../hook/useIsMobile';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 주문 대기 페이지
 */
const OrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    orders,
    ordersPage,
    getOrderLoading,
    getOrders,
    confirmOrders,
    checkConfirm,
      deleteOrders,
  } = useOrderStore();


  const telRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const gridRef = useRef<AgGridReact<IOrderTable>>(null);
  const isMobile = useIsMobile();
  
  // 모바일 선택 상태
  const [mobileSelectedKeys, setMobileSelectedKeys] = useState<Set<string | number>>(new Set());
  const [mobileSelectedRows, setMobileSelectedRows] = useState<IOrderTable[]>([]);

  const { role } = useLoginStore();
  const { myfranchise, getMyFranchise } = useFranchiseStore();
  const isBizProfileEnabled = useMemo(() => Boolean(myfranchise?.bizmProfileKey), [myfranchise?.bizmProfileKey]);
  useEffect(() => { getMyFranchise(); }, [getMyFranchise]);


  const colDefs: ColDef<IOrderTable>[] = [
    {
      headerName: '이름',
      field: 'custInfoNames',
      flex: 3,
    },
    {
      headerName: '이메일',
      field: 'email',
      flex: 6,
    },
    {
      headerName: '연락처' ,
      field: 'telNo' ,
      flex: 4,
      hide: !(role === 'ROLE_ADMIN' || role === 'ROLE_CS' || isBizProfileEnabled),
    },
    {
      headerName: '상품명',
      field: 'productName',
      flex: 4,
      valueFormatter: param => param.value?.replace('_notime', ''),
    },
    {
      headerName: '생성자',
      field: 'createdBy',
      flex: 2,
    },
    {
      headerName: '생성 날짜',
      field: 'createdAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 6,
    },
    {
      headerName: '실시간',
      field: 'realtimeYn',
      flex: 2,
      cellRenderer: (params: any) => {
        if (params.value === 'Y') {
          return <span className="!inline-flex !items-center !px-2 !py-0.5 !rounded-full !text-xs !font-medium !bg-green-100 !text-green-800">⚡ 실시간</span>;
        }
        return <span className="!inline-flex !items-center !px-2 !py-0.5 !rounded-full !text-xs !font-medium !bg-gray-100 !text-gray-600">일반</span>;
      },
    },
  ];

  const getFilteredData = (params?: any) => {
    const telNo = params?.telNo || telRef.current?.value || '';
    const email = params?.email || emailRef.current?.value || '';
    const custName = params?.custName || nameRef.current?.value || '';

    const parsedQuery = queryString.parse(location.search);
    const realtimeYn =
      params?.realtimeYn || (parsedQuery.realtimeYn as 'Y' | 'N') || '';
    const size = params?.size
      ? Number(params.size)
      : parsedQuery.size
      ? Number(parsedQuery.size)
      : 20;

    // 파라미터에 날짜가 있으면 우선 사용, 없으면 URL에서 가져오고, 그것도 없으면 기본값 설정
    let contractStartDt =
      params?.contractStartDt || (parsedQuery.contractStartDt as string);
    let contractEndDt =
      params?.contractEndDt || (parsedQuery.contractEndDt as string);

    // 날짜가 전혀 없으면 기본값 설정 (최근 30일)
    if (!contractStartDt || !contractEndDt) {
      const today = new Date();
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      contractStartDt =
        contractStartDt || formatDate(monthAgo)['yyyy-mm-ddTHH:mm:ss'];
      contractEndDt = contractEndDt || formatDate(today)['yyyy-mm-ddTHH:mm:ss'];
    }

    const queryParams = {
      telNo,
      email,
      custName,
      realtimeYn,
      size,
      contractStartDt,
      contractEndDt,
    };

    updateUrlWithParams(queryParams, navigate);
    getOrders({
      telNo,
      email,
      custName,
      realtimeYn,
      confirmYn: 'N',
      size,
      contractStartDt,
      contractEndDt,
    });
  };

  const onRowDoubleClicked = useCallback(
    (params: RowDoubleClickedEvent<IOrderTable>) => {
      navigate(`/order/list/${params.data?.id}`);
    },
    [navigate]
  );

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const realtimeYn = (parsedQuery.realtimeYn as 'Y' | 'N') || '';
    const telNo = (parsedQuery.telNo as string) || '';
    const email = (parsedQuery.email as string) || '';
    const custName = (parsedQuery.custName as string) || '';
    // 날짜 기본값 설정 (URL에 없으면 최근 7일)
    let contractStartDt = parsedQuery.contractStartDt as string;
    let contractEndDt = parsedQuery.contractEndDt as string;

    if (!contractStartDt || !contractEndDt) {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 30);

      contractStartDt =
        contractStartDt || formatDate(weekAgo)['yyyy-mm-ddTHH:mm:ss'];
      contractEndDt = contractEndDt || formatDate(today)['yyyy-mm-ddTHH:mm:ss'];
    }
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    getOrders({
      realtimeYn,
      telNo,
      email,
      custName,
      confirmYn: 'N',
      contractStartDt,
      contractEndDt,
      page,
      size,
    });
  }, [location.search, getOrders]);

  // 주문 확정 모달
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const showConfirmModal = useCallback(async () => {
    const selectedRows = isMobile ? mobileSelectedRows : gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      try {
        await checkConfirm(idArray);
        setIsOpen(true);
      } catch (error: any) {
        console.error('주문 확정 체크 실패:', error);
        toast.error('주문 확정 정보를 불러오는데 실패했습니다.');
      }
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [checkConfirm, isMobile, mobileSelectedRows]);

  //주문 삭제 모달
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const showDeleteModal = useCallback(() => {
    const selectedRows = isMobile ? mobileSelectedRows : gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      setIsDeleteOpen(true);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [isMobile, mobileSelectedRows]);

  const onClickConfirm = useCallback(() => {
    const selectedRows = isMobile ? mobileSelectedRows : gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      confirmOrders(idArray);
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [confirmOrders, isMobile, mobileSelectedRows]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getOrders({
        telNo: (parsedQuery.telNo as string) || '',
        email: (parsedQuery.email as string) || '',
        custName: (parsedQuery.custName as string) || '',
        confirmYn: 'N',
        contractStartDt: parsedQuery.contractStartDt as string,
        contractEndDt: parsedQuery.contractEndDt as string,
        page,
        size: parseInt(parsedQuery.size as string) || 20,
      });
    },
    [location.search, navigate, getOrders]
  );

  const rowSelection = {
    mode: 'multiRow',
  };

  const onDeleteConfirm = useCallback(async () => {
    const selectedRows = isMobile ? mobileSelectedRows : gridRef.current?.api.getSelectedRows();
    const idArray = selectedRows?.map(row => row.id);

    if (selectedRows && idArray && idArray?.length > 0) {
      await deleteOrders(idArray);
      setIsDeleteOpen(false);
      // 현재 필터 조건 그대로 리스트 재조회
      getFilteredData();
    } else {
      toast.warn('선택된 주문이 없습니다.');
    }
  }, [deleteOrders, getFilteredData, isMobile, mobileSelectedRows]);

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <div className="!w-full">
          <OrderListFilter
            telRef={telRef}
            emailRef={emailRef}
            nameRef={nameRef}
            getFilteredData={getFilteredData}
          />
        </div>
      </div>

      <div className="md:!flex-1 md:!min-h-0">
        {isMobile ? (
          <MobileAccordionTable<IOrderTable>
            data={orders}
            loading={getOrderLoading}
            keyField="id"
            summaryColumns={[
              {
                field: 'realtimeYn',
                render: (value) => value === 'Y' ? (
                  <span className="!px-1.5 !py-0.5 !rounded !text-xs !font-medium !bg-green-100 !text-green-700">⚡</span>
                ) : (
                  <span className="!px-1.5 !py-0.5 !rounded !text-xs !font-medium !bg-gray-100 !text-gray-600">일반</span>
                ),
                className: '!w-10',
              },
              {
                field: 'custInfoNames',
                render: (value) => <span className="!text-sm !font-medium">{value}</span>,
              },
              {
                field: 'productName',
                render: (value) => <span className="!text-xs !text-gray-500">{value?.replace('_notime', '')}</span>,
              },
            ]}
            detailColumns={[
              { field: 'email', label: '이메일' },
              { field: 'telNo', label: '연락처' },
              { field: 'createdBy', label: '생성자' },
              { 
                field: 'createdAt', 
                label: '생성일',
                render: (value) => value ? formatDate(value).timestamp : '-',
              },
            ]}
            onRowClick={(row) => navigate(`/order/list/${row.id}`)}
            emptyMessage="주문 데이터가 없습니다."
            selectable={true}
            selectedKeys={mobileSelectedKeys}
            onSelectionChange={(keys, rows) => {
              setMobileSelectedKeys(keys);
              setMobileSelectedRows(rows);
            }}
          />
        ) : (
          <GridTable<IOrderTable>
            gridRef={gridRef}
            height="100%"
            isLoading={getOrderLoading}
            rowData={orders}
            columnDefs={colDefs}
            rowSelection={rowSelection}
            onRowDoubleClicked={onRowDoubleClicked}
          />
        )}
      </div>

      {/* 페이지네이션 */}
      {ordersPage && (
        <div className="!w-full !mt-4 md:!mt-6 md:!relative">
          {ordersPage.totalPages > 1 && (
            <div className="!flex !justify-center !items-center !space-x-1 !py-3">
              {/* 맨 처음으로 */}
              <button
                onClick={() => onPageChange(0)}
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  className="!w-3 !h-3 !-ml-1"
                />
              </button>

              {/* 이전 페이지 */}
              <button
                onClick={() =>
                  onPageChange(Math.max(0, ordersPage.pageable.pageNumber - 1))
                }
                disabled={ordersPage.pageable.pageNumber === 0}
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              </button>

              {/* 페이지 번호들 */}
              <div className="!flex !space-x-0.5">
                {(() => {
                  const currentPage = ordersPage.pageable.pageNumber;
                  const totalPages = ordersPage.totalPages;
                  const maxVisible = isMobile ? 5 : 10;
                  const half = Math.floor(maxVisible / 2);
                  
                  let startPage = Math.max(0, currentPage - half);
                  let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

                  if (endPage - startPage < maxVisible - 1) {
                    startPage = Math.max(0, endPage - maxVisible + 1);
                  }

                  return Array.from(
                    { length: endPage - startPage + 1 },
                    (_, i) => startPage + i
                  ).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                        pageNum === currentPage
                          ? '!bg-blue-600 !text-white'
                          : 'hover:!bg-gray-100 !text-gray-700'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  ));
                })()}
              </div>

              {/* 다음 페이지 */}
              <button
                onClick={() =>
                  onPageChange(
                    Math.min(
                      ordersPage.totalPages - 1,
                      ordersPage.pageable.pageNumber + 1
                    )
                  )
                }
                disabled={
                  ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
                }
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              </button>

              {/* 맨 마지막으로 */}
              <button
                onClick={() => onPageChange(ordersPage.totalPages - 1)}
                disabled={
                  ordersPage.pageable.pageNumber === ordersPage.totalPages - 1
                }
                className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
              >
                <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="!w-3 !h-3 !-ml-1"
                />
              </button>
            </div>
          )}

          {/* 버튼 영역 (데스크톱: 우측, 모바일: 하단 중앙) */}
          <div className="!flex !gap-2 !justify-center md:!justify-end !mt-2 md:!mt-0 md:!absolute md:!right-0 md:!top-1/2 md:!-translate-y-1/2">
            <button
                type="button"
                onClick={showDeleteModal}
                className="!px-4 !py-2 !bg-red-500 hover:!bg-red-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
            >
              주문 삭제
            </button>
            <button
              type="button"
              onClick={showConfirmModal}
              className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
            >
              주문 확정
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onClickConfirm={onClickConfirm}
      />
      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={onDeleteConfirm}
          title="주문 삭제 확인"
          message="정말로 이 주문을 삭제하시겠습니까?
삭제된 주문은 복구할 수 없습니다."
          confirmText="삭제"
          cancelText="취소"
          type="danger"
      />
    </div>
  );
};

export default OrderList;
